/**
 * Groupe les codes défauts d'une grille par famille (B / C / P / U),
 * insère un séparateur par famille et trie chaque famille par ordre croissant.
 * Se rejoue après la fusion des pages et masque les séparateurs vides pendant une recherche.
 */
const GROUPS = [
  { letter: 'b', label: 'Codes B', hint: 'Carrosserie & habitacle' },
  { letter: 'c', label: 'Codes C', hint: 'Châssis, ABS & ESP' },
  { letter: 'p', label: 'Codes P', hint: 'Moteur & transmission' },
  { letter: 'u', label: 'Codes U', hint: 'Réseau & communication' }
]

const SEPARATOR_SELECTOR = '[data-codes-group-separator]'

/**
 * @param {{ letter: string; label: string; hint: string }} group
 * @returns {HTMLDivElement}
 */
const createGroupSeparator = (group) => {
  const sep = document.createElement('div')
  sep.className = 'content-grid-separator'
  sep.setAttribute('data-codes-group-separator', group.letter)
  sep.setAttribute('role', 'heading')
  sep.setAttribute('aria-level', '2')

  const startLine = document.createElement('span')
  startLine.className = 'content-grid-separator__line'
  startLine.setAttribute('aria-hidden', 'true')

  const label = document.createElement('span')
  label.className = 'content-grid-separator__label'
  label.append(group.label)

  const hint = document.createElement('span')
  hint.className = 'content-grid-separator__hint'
  hint.append(` · ${group.hint}`)
  label.appendChild(hint)

  const endLine = document.createElement('span')
  endLine.className = 'content-grid-separator__line'
  endLine.setAttribute('aria-hidden', 'true')

  sep.append(startLine, label, endLine)
  return sep
}

/** @param {HTMLElement} item */
export const codeOf = (item) =>
  (item.getAttribute('data-code') || item.textContent || '')
    .trim()
    .toLowerCase()

/** @param {HTMLElement} grid @param {string} itemSelector */
export const regroup = (grid, itemSelector) => {
  const items = Array.from(grid.querySelectorAll(itemSelector)).filter(
    (n) => n instanceof HTMLElement
  )
  if (items.length === 0) return
  for (const sep of grid.querySelectorAll(SEPARATOR_SELECTOR)) sep.remove()

  items.sort((a, b) =>
    codeOf(a).localeCompare(codeOf(b), 'fr', { numeric: false })
  )

  const fragment = document.createDocumentFragment()
  const known = new Set(GROUPS.map((g) => g.letter))
  const buckets = new Map(GROUPS.map((g) => [g.letter, []]))
  const others = []
  for (const item of items) {
    const l = codeOf(item).charAt(0)
    if (known.has(l)) buckets.get(l).push(item)
    else others.push(item)
  }

  let order = 0
  for (const g of GROUPS) {
    const list = buckets.get(g.letter)
    if (!list || list.length === 0) continue
    fragment.appendChild(createGroupSeparator(g))
    for (const item of list) {
      item.setAttribute('data-codes-group', g.letter)
      item.style.setProperty('--metaobject-item-reveal-order', String(order++))
      fragment.appendChild(item)
    }
  }
  for (const item of others) fragment.appendChild(item)
  grid.replaceChildren(fragment)
  syncSeparators(grid, itemSelector)
}

/** Masque un séparateur dont tous les codes sont cachés par la recherche. */
export const syncSeparators = (grid, itemSelector) => {
  for (const sep of grid.querySelectorAll(SEPARATOR_SELECTOR)) {
    const letter = sep.getAttribute('data-codes-group-separator')
    const visible = Array.from(
      grid.querySelectorAll(`${itemSelector}[data-codes-group="${letter}"]`)
    ).some((i) => i instanceof HTMLElement && !i.hidden)
    sep.hidden = !visible
  }
}

/** @type {WeakSet<HTMLElement>} */
const ready = new WeakSet()

/** @param {HTMLElement} section */
export const initCodesGridGroups = (section) => {
  const grid = section.querySelector('[data-metaobject-grid-search-grid]')
  const itemSelector =
    section.getAttribute('data-metaobject-grid-search-item-selector') ||
    '.content-item'
  if (!(grid instanceof HTMLElement)) return

  const pages = Number.parseInt(
    grid.dataset.metaobjectGridPaginationPages || '1',
    10
  )
  const loaded = section.dataset.metaobjectGridPaginationLoaded === 'true'
  if (pages <= 1 || loaded) regroup(grid, itemSelector)

  if (ready.has(section)) return
  ready.add(section)

  section.addEventListener('metaobject-grid:refresh', () =>
    regroup(grid, itemSelector)
  )

  let raf = 0
  const observer = new MutationObserver((records) => {
    if (
      !records.some(
        (r) => r.target instanceof HTMLElement && r.target.matches(itemSelector)
      )
    )
      return
    window.cancelAnimationFrame(raf)
    raf = window.requestAnimationFrame(() => syncSeparators(grid, itemSelector))
  })
  observer.observe(grid, {
    attributes: true,
    attributeFilter: ['hidden'],
    subtree: true
  })
}

export const bootCodesGridGroups = () => {
  for (const s of document.querySelectorAll('[data-codes-grid-groups]')) {
    if (s instanceof HTMLElement) initCodesGridGroups(s)
  }
}
if (document.readyState === 'loading')
  document.addEventListener('DOMContentLoaded', bootCodesGridGroups)
else bootCodesGridGroups()
document.addEventListener('shopify:section:load', bootCodesGridGroups)
